import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Box,
  LinearProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import Papa from "papaparse";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

export default function UploadRoom() {
  const [previewData, setPreviewData] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [examRooms, setExamRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const user = JSON.parse(sessionStorage.getItem("user"));

  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const user_email = user?.user_email;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // CSV headers (uploading with block_name, floor_name instead of IDs)
  const csvHeaders = [
    "room_id",
    "college_id",
    "block_name",
    "floor_name",
    "room_slno",
    "room_no",
    "room_rows",
    "room_columns",
    "room_strength",
    "room_type",
    "remarks",
    "status(ACTIVE/INACTIVE)",
    "delete_status(0/1)",
  ];
  const fetchExamRooms = async () => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    setLoadingRooms(true);

    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/exam-rooms-upload/${user.client_id}/${user.college_id}`
      );
      setExamRooms(res.data.data || []);
    } catch (err) {
      console.error(err);
      setMessage("Failed to fetch exam rooms");
    }

    setLoadingRooms(false);
  };

  useEffect(() => {
    fetchExamRooms();
  }, []);
  // Download template CSV
  const handleDownloadTemplate = () => {
    const csvContent = csvHeaders.join(",") + "\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "exam_rooms_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // DataGrid columns (add S.No and Errors)
  const columns = [
    { field: "sno", headerName: "S.No", width: 70 },
    { field: "room_id", headerName: "Room ID", width: 100 },
    { field: "college_id", headerName: "College ID", width: 100 },
    { field: "block_name", headerName: "Block Name", width: 160 },
    { field: "floor_name", headerName: "Floor Name", width: 160 },
    { field: "room_slno", headerName: "Room S.No", width: 120 },
    { field: "room_no", headerName: "Room No", width: 120 },
    { field: "room_rows", headerName: "Rows", width: 100 },
    { field: "room_columns", headerName: "Columns", width: 100 },
    { field: "room_strength", headerName: "Strength", width: 120 },
    { field: "room_type", headerName: "Room Type", width: 150 },
    { field: "remarks", headerName: "Remarks", width: 180 },
    { field: "status", headerName: "Status", width: 120 },
    { field: "delete_status", headerName: "Delete Status", width: 130 },
    {
      field: "errors",
      headerName: "Errors",
      width: 250,
      renderCell: (params) => (
        <span style={{ color: "red" }}>
          {params.value && params.value.length > 0 ? params.value.join(", ") : ""}
        </span>
      ),
    },
  ];

  // File selection
  const handleFileChange = (e) => {
    setCsvFile(e.target.files[0]);
    e.target.value = null;
  };

  // Parse and validate
  const handlePreview = () => {
    if (!csvFile) {
      alert("Please choose a CSV file first!");
      return;
    }

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        const validatedData = results.data.map((row, index) => {
          let errors = [];

          // Validation rules
          if (!row.block_name) errors.push("Missing block_name");
          if (!row.floor_name) errors.push("Missing floor_name");
          if (!row.room_no) errors.push("Missing room_no");
          if (row.room_strength && isNaN(row.room_strength)) errors.push("Invalid strength");
          if (row.room_rows && isNaN(row.room_rows)) errors.push("Invalid rows");
          if (row.room_columns && isNaN(row.room_columns)) errors.push("Invalid columns");

          return {
            id: index + 1,
            sno: index + 1,
            ...row,
            errors,
          };
        });
        setPreviewData(validatedData);
      },
    });
  };

  // Confirm upload
  const handleConfirm = async () => {
    try {
      const validRows = previewData.filter((row) => !row.errors || row.errors.length === 0);
      if (validRows.length === 0) {
        alert("No valid rows to upload!");
        return;
      }
      setUploading(true);
      setMessage("");
      const rowsWithUserEmail = validRows.map((row) => ({
        ...row,
        created_by: user_email, // add created_by
        modified_by: user_email, // optionally also modified_by
      }));

      const res = await axios.post(
        `${API_BASE_URL}/api/exam-rooms-upload/${clientId}/${collegeId}/data`,
        { exam_rooms: rowsWithUserEmail }
      );

      setMessage(res.data.message || "Room details uploaded successfully!");
      setPreviewData([]);
      setCsvFile(null);
    } catch (err) {
      if (err.response) {
        setMessage(err.response.data?.message || "Upload failed");
      } else if (err.request) {
        setMessage("No response from server. Please try again later.");
      } else {
        setMessage(`Upload failed: ${err.message}`);
      }
    }
    setUploading(false);
  };
  const handleDeleteClick = (room) => {
    setSelectedRoom(room);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/exam-rooms-upload/${clientId}/${collegeId}/${selectedRoom.room_id}`
      );

      const { success, message } = response.data;

      if (success) {
        // Remove deleted room from state
        setExamRooms((prev) => prev.filter((room) => room.room_id !== selectedRoom.room_id));
        setDeleteDialogOpen(false);
        setSelectedRoom(null);

        // Optionally show success notification
        setMessage(response.data.message || "Room deleted successfully");
      } else {
        // Show error message from server
        setMessage(response.data.message || "Failed to delete room");
      }
    } catch (error) {
      console.error("Failed to delete room:", error);

      // Handle network/server errors
      setMessage(error.response?.data?.message || "An error occurred while deleting the room");
    }
    setDeleteDialogOpen(false);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSelectedRoom(null);
  };
  const handleToggleStatus = async (room) => {
    try {
      const newStatus = room.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

      const response = await axios.put(
        `${API_BASE_URL}/api/exam-rooms-upload/${clientId}/${collegeId}/${room.room_id}/toggle-status`,
        { status: newStatus }
      );

      if (response.data.success) {
        setExamRooms((prev) =>
          prev.map((r) => (r.room_id === room.room_id ? { ...r, status: newStatus } : r))
        );

        setMessage(`Room status changed to ${newStatus}`);
      } else {
        setMessage("Failed to update status");
      }
    } catch (error) {
      console.error("Status update failed:", error);
      setMessage(error.response?.data?.message || "Error updating status");
    }
  };

  const handleToggleUsage = async (room) => {
    try {
      const newUsage = room.room_usage === "REGULAR" ? "SUPPLY" : "REGULAR";

      const response = await axios.put(
        `${API_BASE_URL}/api/exam-rooms-upload/${clientId}/${collegeId}/${room.room_id}/toggle-room_usage`,
        { room_usage: newUsage }
      );

      if (response.data.success) {
        setExamRooms((prev) =>
          prev.map((r) => (r.room_id === room.room_id ? { ...r, room_usage: newUsage } : r))
        );

        setMessage(`Room Usage changed to ${newUsage}`);
      } else {
        setMessage("Failed to update usage");
      }
    } catch (error) {
      console.error("Usage update failed:", error);
      setMessage(error.response?.data?.message || "Error updating room usage");
    }
  };

  const roomColumns = [
    { field: "sno", headerName: "S.No", width: 70 },
    { field: "block_name", headerName: "Block Name", width: 150 },
    { field: "floor_name", headerName: "Floor Name", width: 150 },
    { field: "room_no", headerName: "Room No", width: 120 },
    { field: "room_rows", headerName: "Rows", width: 80 },
    { field: "room_columns", headerName: "Columns", width: 80 },
    { field: "room_strength", headerName: "Strength", width: 80 },
    {
      field: "room_usage",
      headerName: "Room Usage",
      width: 120,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          color={params.row.room_usage === "REGULAR" ? "success" : "warning"}
          onClick={() => handleToggleUsage(params.row)}
          sx={{
            color: params.row.room_usage === "REGULAR" ? "#c31111ff" : "#000", // font color here
          }}
        >
          {params.row.room_usage}
        </Button>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          color={params.row.status === "ACTIVE" ? "success" : "warning"}
          onClick={() => handleToggleStatus(params.row)}
          sx={{
            color: params.row.room_usage === "REGULAR" ? "#131aa8ff" : "#000", // font color here
          }}
        >
          {params.row.status}
        </Button>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => handleDeleteClick(params.row)}
          >
            Delete
          </Button>
        );
      },
    },
  ];
  return (
    <Box sx={{ p: 3 }}>
      {/* Download Template */}
      <Button
        variant="contained"
        startIcon={<DownloadIcon />}
        onClick={handleDownloadTemplate}
        sx={{
          mt: 1,
          backgroundColor: "#388e3c",
          color: "#fff",
          "&:hover": { backgroundColor: "#2c6b2f" },
        }}
      >
        Download CSV
      </Button>

      {/* File Upload */}
      <input
        type="file"
        accept=".csv"
        style={{ display: "none" }}
        id="upload-csv"
        onChange={handleFileChange}
      />
      <label htmlFor="upload-csv">
        <Button
          variant="contained"
          component="span"
          startIcon={<CloudUploadIcon />}
          sx={{
            ml: 2,
            mt: 1,
            backgroundColor: "#1976d2",
            color: "#fff",
            "&:hover": { backgroundColor: "#115293" },
          }}
        >
          Choose CSV
        </Button>
      </label>

      <Button
        sx={{
          ml: 2,
          mt: 1,
          backgroundColor: "#ff9800",
          color: "#fff",
          "&:hover": { backgroundColor: "#c77700" },
        }}
        variant="contained"
        onClick={handlePreview}
      >
        Preview
      </Button>
      <Button
        sx={{
          ml: 2,
          mt: 1,
          backgroundColor: "#4caf50",
          color: "#fff",
          "&:hover": { backgroundColor: "#357a38" },
        }}
        variant="contained"
        onClick={handleConfirm}
      >
        Confirm & Upload
      </Button>

      {csvFile && (
        <Typography variant="body1" sx={{ mb: 2 }}>
          Selected: {csvFile.name}
        </Typography>
      )}
      {uploading && <LinearProgress sx={{ mt: 2 }} />}

      {message && (
        <Alert severity={message.includes("success") ? "success" : "error"} sx={{ mt: 2 }}>
          {message}
        </Alert>
      )}
      {previewData.length > 0 && (
        <>
          <Box sx={{ mt: 3, height: 500 }}>
            <DataGrid
              rows={previewData}
              columns={columns}
              getRowId={(row) => row.sno}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              disableRowSelectionOnClick
              pagination
              disableSelectionOnClick
              disableColumnMenu
              autoHeight
            />
          </Box>
        </>
      )}

      {examRooms.length > 0 && (
        <>
          <Box sx={{ mt: 3, width: "100%", height: 700 }}>
            <DataGrid
              rows={examRooms.map((room, index) => ({
                id: index + 1,
                sno: index + 1,
                room_id: room.room_id,
                college_id: room.college_id,
                block_name: room.block?.block_name || "",
                floor_name: room.floor?.floor_name || "",
                room_no: room.room_no,
                room_slno: room.room_slno,
                room_rows: room.room_rows,
                room_columns: room.room_columns,
                room_strength: room.room_strength,
                room_type: room.room_type,
                remarks: room.remarks,
                room_usage: room.room_usage,
                status: room.status,
                delete_status: room.delete_status,
              }))}
              columns={roomColumns}
              loading={loadingRooms}
              pageSize={pageSize} // controlled pageSize
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)} // update state
              rowsPerPageOptions={[5, 10, 25, 50]}
              disableRowSelectionOnClick
              pagination
              autoHeight
              disableSelectionOnClick
            />
          </Box>
        </>
      )}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete Exam Room</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete room {selectedRoom?.room_no}? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
